/**
 * Context Usage Bar Component
 *
 * Visual indicator showing how much of OpenAI's context window is used.
 * Shows progressive warnings as the conversation grows.
 */

import { AlertTriangle, HelpCircle, Info, Loader2, Minimize2, XCircle } from 'lucide-react';
import React from 'react';
import { cn } from '../utils/cn';
import { isDevelopment } from '../env';
import { usePlatform } from '../contexts/platform-context';
import { useShowContextBar } from '../stores/system-settings-store';
import {
  ContextUsageStatus,
  formatTokenCount,
  getContextUsage,
  getContextWarningMessage,
  OPENAI_LIMITS,
} from '../utils/token-estimation';
import { Tooltip } from './tooltip';
import { TokenExplanationModal } from './token-explanation-modal';

export type ContextUsageBarProps = {
  estimatedTokens: number;
  className?: string;
  showDetails?: boolean; // Show token numbers
  compact?: boolean; // Minimal design
  onShrinkContext?: () => void;
  isShrinking?: boolean;
};

type ContextUsageColorScheme = {
  bar: string;
  bg: string;
  text: string;
  icon: React.ElementType;
};

export const ContextUsageBar: React.FC<ContextUsageBarProps> = ({
  estimatedTokens,
  className,
  showDetails = true,
  compact = false,
  onShrinkContext,
  isShrinking = false,
}) => {
  // In the browser extension, the web app loads inside a narrow iframe (?context=sidebar).
  // Detect this so the compress button shows as icon-only to save horizontal space.
  const { isSidebar } = usePlatform();
  const showContextBar = useShowContextBar();
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  if (!showContextBar) return null;
  const usage = getContextUsage(estimatedTokens);
  const warningMessage = getContextWarningMessage(usage.status);

  // Color scheme for percentage bar based on status
  const getColors: () => ContextUsageColorScheme = () => {
    const colorStatusHash: Record<ContextUsageStatus, ContextUsageColorScheme> = {
      exceeded: {
        bar: 'bg-red-500',
        bg: 'bg-muted-foreground/10',
        text: 'text-danger-500',
        icon: XCircle,
      },
      critical: {
        bar: 'bg-orange-500',
        bg: 'bg-muted-foreground/10',
        text: 'text-orange-500',
        icon: AlertTriangle,
      },
      warning: {
        bar: 'bg-yellow-500',
        bg: 'bg-muted-foreground/10',
        text: 'text-warning-500',
        icon: AlertTriangle,
      },
      safe: {
        bar: 'bg-blue-500',
        bg: 'bg-muted-foreground/10',
        text: 'text-secondary-500',
        icon: Info,
      },
    };
    return colorStatusHash[usage.status] || colorStatusHash['safe'];
  };

  const colors = getColors();
  const Icon = colors.icon;
  const percentageDisplay = Math.min(Math.round(usage.percentage * 100), 100);

  if (compact) {
    return (
      <Tooltip
        content={`${formatTokenCount(estimatedTokens)} / ${formatTokenCount(OPENAI_LIMITS.GPT4_VISION_CONTEXT)} tokens${warningMessage ? ` • ${warningMessage}` : ''}`}
        side="bottom"
      >
        <div className={cn('flex items-center gap-2', className)}>
          <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={cn('h-full transition-all duration-300', colors.bar)}
              style={{ width: `${Math.min(usage.percentage * 100, 100)}%` }}
            />
          </div>
          {usage.status !== 'safe' && <Icon className={cn('w-3.5 h-3.5', colors.text)} />}
        </div>
      </Tooltip>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Progress bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className={cn('h-2 rounded-full overflow-hidden', colors.bg)}>
            <div
              className={cn(
                'h-full transition-all duration-300',
                colors.bar,
                usage.status === 'exceeded' && 'animate-pulse'
              )}
              style={{ width: `${Math.min(usage.percentage * 100, 100)}%` }}
            />
          </div>
        </div>

        {showDetails && (
          <span className={cn('text-xs font-medium tabular-nums', colors.text)}>
            {percentageDisplay}%
          </span>
        )}
      </div>

      {/* Details and warning */}
      {showDetails && (
        <div className="flex items-start justify-between gap-2 text-xs">
          <div className={cn('flex items-center gap-1.5', colors.text)}>
            <div className="flex items-center gap-1">
              <Icon className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                {formatTokenCount(estimatedTokens)} /{' '}
                {formatTokenCount(OPENAI_LIMITS.GPT4_VISION_CONTEXT)} tokens
                {usage.tokensRemaining > 0 && (
                  <span className="text-muted-foreground ml-1">
                    ({formatTokenCount(usage.tokensRemaining)} remaining)
                  </span>
                )}
              </span>
            </div>

            {warningMessage && (
              <span className={cn('text-right font-medium', colors.text)}>{warningMessage}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onShrinkContext &&
              (isDevelopment || usage.percentage >= OPENAI_LIMITS.WARNING_THRESHOLD) && (
                <Tooltip
                  content={isShrinking ? 'Compressing…' : 'Compress context'}
                  side="bottom"
                  disabled={!isSidebar}
                >
                  <button
                    onClick={onShrinkContext}
                    disabled={isShrinking}
                    className={cn(
                      'p-0.5 rounded-md',
                      'text-muted-foreground hover:text-foreground',
                      'hover:bg-muted/50',
                      'transition-colors',
                      'flex items-center gap-1',
                      isShrinking && 'opacity-60 cursor-not-allowed'
                    )}
                    aria-label="Compress conversation context"
                  >
                    {isShrinking ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Minimize2 className="w-3.5 h-3.5" />
                    )}
                    {!isSidebar && (isShrinking ? 'Compressing…' : 'Compress context')}
                  </button>
                </Tooltip>
              )}
            <button
              onClick={() => setIsModalOpen(true)}
              className={cn(
                'p-0.5 rounded-md',
                'text-muted-foreground hover:text-foreground',
                'hover:bg-muted/50',
                'transition-colors',
                'flex items-center gap-1'
              )}
              aria-label="Learn about tokens"
            >
              <HelpCircle className="w-3.5 h-3.5" />
              What are tokens?
            </button>
          </div>
        </div>
      )}

      {/* Token Explanation Modal */}
      <TokenExplanationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

ContextUsageBar.displayName = 'ContextUsageBar';
